import { Form } from "antd";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { PolicyForm } from "./PolicyFormDialog.default";
import { useEffect, useState } from "react";
import {
  addCompanyPolicy,
  deleteCompanyPolicy,
  getCompanies,
  updateCompanyPolicy,
  updateCompanyPolicyFile,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import PolicyAccessPreview from "./PolicyAccessPreview";
import InModal from "../controls/InModal";
import { setFormFields } from "../utils/setFormFields";
import { hasFormTouched } from "../utils/hasFormChanged";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { createSearchRequest } from "../hooks/useSearchRequest";

export interface PolicyFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: PolicyForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function PolicyFormDialog(props: PolicyFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<PolicyForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Only admins choose a policy's scope (platform-wide vs a specific company); members author for their own company.
  const isAdmin = useSelector(authorizeSelectors.isAppAdmin);

  const companiesRequest = createSearchRequest(getCompanies, "name");

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        setFormFields(form, props.initialValues);
      } else {
        form.setFieldsValue(props.initialValues);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { name, code, pdfFiles, company } = values;
    setLoading(true);
    setError(false);
    if (props.mode === "add") {
      addCompanyPolicy({
        name,
        code,
        pdfFile: pdfFiles[0],
        // Admin picked a company ⇒ company-scoped; left empty ⇒ platform-wide.
        companyUid: isAdmin ? company?.value : undefined,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        const promises = [
          updateCompanyPolicy({
            uid: props.initialValues?.uid,
            rowVersion: props.initialValues?.rowVersion,
            name,
            code,
            pdfFile: pdfFiles ? pdfFiles[0] : undefined,
          }),
        ];
        if (pdfFiles?.length) {
          promises.push(
            updateCompanyPolicyFile({
              uid: props.initialValues?.uid,
              pdfFile: pdfFiles[0],
            }),
          );
        }
        Promise.all(promises)
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch(() => {
            setLoading(false);
            setError(true);
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteCompanyPolicy(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setError(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Policy", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [
                      {
                        required: true,
                        message: "Name is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "code",
                    label: "Code",
                    rules: [
                      {
                        required: true,
                        message: "Code is required",
                      },
                    ],
                  },
                  // Admins choose the scope on create: pick a company, or leave empty for a platform-wide policy.
                  ...(isAdmin && props.mode === "add"
                    ? [
                        {
                          type: "combobox" as const,
                          name: "company",
                          label: "Company (leave empty for platform-wide)",
                          request: companiesRequest,
                        },
                      ]
                    : []),
                  // On manage/edit the scope is fixed — surface it read-only so the editor sees what they're changing.
                  ...(props.mode === "update"
                    ? [
                        {
                          type: "labelbox" as const,
                          name: "scope",
                          label: "Scope",
                        },
                      ]
                    : []),
                  {
                    type: "pdf",
                    name: "pdfFiles",
                    label: "Pdf File",
                    limit: 50,
                    accept: "application/pdf",
                    rules: [
                      {
                        required: props.mode === "add" ? true : false,
                        message: "Pdf file is required",
                        validator(_, value) {
                          if (
                            (value || [])[0] != null ||
                            props.initialValues?.pdfUrl != null
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("Pdf file is required"),
                          );
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode !== "delete" &&
        ((props.mode === "add" && isAdmin) || props.mode === "update") && (
          <PolicyAccessPreview
            scopeCompanyUid={
              props.mode === "add"
                ? values?.company?.value
                : props.initialValues?.companyUid
            }
          />
        )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="policy"
          entityName={props.initialValues?.name}
        />
      )}
      {error && <ModalErrorAlert message="Something went wrong, try again" />}
    </InModal>
  );
}

export default PolicyFormDialog;
